import {MegaphoneIcon, MicromorgiIcon, SurprisesIcon} from '@/assets/icons/icons'
import {Popover} from '@/components/ui/popover/Popover'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useMe} from '@/features/authentication/queries/useMe'
import {GuestTypeV3} from '@/features/authentication/types'
import {BillingInfoModal} from '@/features/billing/components/billing-info-modal/BillingInfoModal'
import {useGetMembershipPlans} from '@/features/configure-membership-plans/edit-configure-membership-plans-modal/queries/useGetMembershipPlans'
import {DonationType} from '@/features/recurring-gift/types'
import {Translate} from '@/features/translation/components/translate/Translate'
import useMixPanel from '@/integrations/mixpanel/hooks/useMixpanel'
import {MixpanelEventNameE} from '@/integrations/mixpanel/types'
import {TagManager} from '@/integrations/tag-manager/tagManager'
import {useRootStore} from '@/store'
import {selectChoosePaymentsMethodModal, selectErrorModal, selectRecurringGiftModal} from '@/store/selectors'
import {USER_ROLE_GUEST} from '@/utilities/constants/user'
import {SplideTrack} from '@splidejs/react-splide'
import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {remapCurrency} from '../../constants'
import CurrentMembershipPlan from './components/CurrentMembershipPlan'
import {
    StyledActivePlanIcon,
    StyledActiveSubscrTooltipContent,
    StyledButton,
    StyledButtonContent,
    StyledContainer,
    StyledContentBox,
    StyledContentContainer,
    StyledCurrentPlan,
    StyledDiscountText,
    StyledImageContainer,
    StyledInfoBox,
    StyledInfoContainer,
    StyledInfoIcon,
    StyledNameBox,
    StyledOpenGiftIcon,
    StyledPercentageOfDiscount,
    StyledPopoverContent,
    StyledSplide,
    StyledSplideSlide,
    StyledTooltipBox,
    StyledUpdatePlanButton
} from './style'
import {GiftModalSuccess} from './SuccessModal'
import {SubscriptionStatusesE} from '@/features/chat/chat.model'
import {useGetCreatorProfile} from '@/features/guest/queries/useGetCreatorProfile'
import {useSubscribeToHostV3} from '@/features/recurring-gift/queries/useSubscribeToHostV3'
import {useGetSubscribedHostsV3} from '@/features/recurring-gift/queries/useGetSubscribedHostsV3'

export const MemberShipPlans = ({hostId}: {hostId: number}) => {
    const {t} = useTranslation()
    const mixpanel = useMixPanel()
    const theme = useTheme()
    const {data} = useMe()
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [selectedMembership, setSelectedMembership] = useState<DonationType>()
    const [viewableSlide, setViewableSlide] = useState<number>(0)
    const {data: packagesListForGuest, isLoading: isLoadingGuest} = useGetMembershipPlans(`${hostId}`)
    const {openModal: openChoosePaymentsMethodModal} = useRootStore(selectChoosePaymentsMethodModal)
    const {openErrorModal} = useRootStore(selectErrorModal)
    const {data: subscriptionHosts} = useGetSubscribedHostsV3({
        enabled: true,
        status: SubscriptionStatusesE.ACTIVE
    })
    const user = data?.type === USER_ROLE_GUEST ? (data as GuestTypeV3) : null
    const hasBillingData = !!user?.typeAttributes?.hasInvoicingProfile
    const handleToggleShowSuccessModal = () => {
        setShowSuccessModal(!showSuccessModal)
    }
    const {data: host} = useGetCreatorProfile({creatorIdentifier: hostId.toString() ?? null})

    const [showBillingInfoModal, setShowBillingInfoModal] = useState(false)

    const currencySymbol = remapCurrency[data?.currency || 'default']

    const {
        mutate: subscribeToHost,
        data: subscribeToHostResponse,
        error: subscribeToHostError,
        isPending: isPendingSubscribeToHost
    } = useSubscribeToHostV3()
    const {openModal: openSelectPlanModal} = useRootStore(selectRecurringGiftModal)
    const correctPackages = packagesListForGuest
        ?.filter(pkg => pkg?.group?.status === 'active' && !!pkg.group)
        .sort((a, b) => Number(Boolean(b.discount)) - Number(Boolean(a.discount)))
    const activeSubscription = subscriptionHosts?.find(
        subsriptionsHost => subsriptionsHost.rookieId.toString() === `${hostId}`
    )
    const hasCoupon = Boolean(correctPackages?.[viewableSlide]?.discount)
    let countOfExclusiveGroupsInActivePackage: null | number = null
    const activePackage =
        activeSubscription && correctPackages
            ? correctPackages.find(({amount}, index) => {
                  if (amount === activeSubscription.amount) {
                      countOfExclusiveGroupsInActivePackage = index + 1
                  }
                  return amount === activeSubscription.amount
              })
            : null
    const onSuccess = () => {
        if (!hasBillingData) {
            return
        } else {
            TagManager.dataLayer({
                event: 'giftmenu_pop_click_redirect'
            })
            user &&
                hostId &&
                selectedMembership &&
                subscribeToHost({
                    hostID: +hostId,
                    subscriptionId: +selectedMembership?.id,
                    userDiscountId: correctPackages?.[viewableSlide]?.discount?.id
                })
        }
    }

    useEffect(() => {
        if (subscribeToHostError) {
            if (subscribeToHostError.response?.status === 303) {
                openChoosePaymentsMethodModal({
                    apiCallback: onSuccess,
                    paymentMethodUrls: subscribeToHostError.response?.data?.url ?? null,
                    description: 'choose_payment_method:description_gift',
                    currencyAmount: selectedMembership?.amount,
                    rookieName: host?.typeAttributes.firstName,
                    isMonthlySupport: true
                })
                mixpanel?.trackEvent(MixpanelEventNameE.CheckoutContinue)
            } else {
                openErrorModal({message: subscribeToHostError.message || null})
            }
        }
        if (subscribeToHostResponse) {
            handleToggleShowSuccessModal()
        }
    }, [subscribeToHostResponse, subscribeToHostError, hasBillingData])

    return (
        <>
            {(isPendingSubscribeToHost || isLoadingGuest) && <Spinner />}
            {!!correctPackages && correctPackages?.length >= 1 && (
                <div>
                    <StyledNameBox>{t('rookie_my_profile:membership:title')}</StyledNameBox>
                    {activePackage && countOfExclusiveGroupsInActivePackage ? (
                        <StyledCurrentPlan>
                            {activePackage.group.url && (
                                <StyledImageContainer
                                    hasDefaultImage={
                                        activePackage.group.url?.includes('default_group_image.png') || false
                                    }
                                >
                                    <StyledOpenGiftIcon />
                                    <img src={activePackage.group.url} />
                                </StyledImageContainer>
                            )}
                            <CurrentMembershipPlan
                                currentPlan={activePackage}
                                countOfExclusiveGroups={countOfExclusiveGroupsInActivePackage}
                            />
                            <StyledUpdatePlanButton
                                variant="outlined"
                                onClick={() => {
                                    openSelectPlanModal({hostID: String(hostId), selectedAmount: activePackage})
                                }}
                            >
                                {t('rookie_my_profile:membership:upgrade_membership')}
                            </StyledUpdatePlanButton>
                        </StyledCurrentPlan>
                    ) : (
                        <StyledContentContainer hasCoupon={hasCoupon}>
                            <StyledSplide
                                options={{rewind: true, arrows: false, gap: 32, direction: theme.direction}}
                                hasTrack={false}
                                onMove={(_: unknown, idx: number) => {
                                    setViewableSlide(idx)
                                }}
                            >
                                <SplideTrack>
                                    {correctPackages?.map((plan, index) => {
                                        const hasDefaultImage =
                                            plan.group.url?.includes('default_group_image.png') || false
                                        return (
                                            <StyledSplideSlide key={index}>
                                                <StyledContentBox>
                                                    {plan.group.url && (
                                                        <StyledImageContainer
                                                            hasDefaultImage={hasDefaultImage}
                                                            hasCoupon={hasCoupon}
                                                        >
                                                            {hasCoupon && (
                                                                <StyledPercentageOfDiscount>
                                                                    {t(
                                                                        'rookie_my_profile:membership:for_you_percentage_off',
                                                                        {
                                                                            PERCENTAGE: plan.discount
                                                                                ? +plan.discount?.discount_percentage *
                                                                                  100
                                                                                : 0
                                                                        }
                                                                    )}
                                                                </StyledPercentageOfDiscount>
                                                            )}

                                                            <StyledOpenGiftIcon />
                                                            <img src={plan.group.url} />
                                                        </StyledImageContainer>
                                                    )}
                                                    {activeSubscription?.amount === plan.amount && (
                                                        <Popover
                                                            isModal={true}
                                                            withArrow={true}
                                                            triggerProps={{
                                                                asChild: true
                                                            }}
                                                            trigger={
                                                                <StyledActivePlanIcon>
                                                                    <SurprisesIcon />
                                                                </StyledActivePlanIcon>
                                                            }
                                                        >
                                                            <StyledActiveSubscrTooltipContent>
                                                                {t('rookie_my_profile:membership:tooltip')}
                                                            </StyledActiveSubscrTooltipContent>
                                                        </Popover>
                                                    )}
                                                    <StyledContainer>
                                                        <StyledInfoContainer>
                                                            <StyledInfoBox>
                                                                <MegaphoneIcon />
                                                                {plan?.group.name}

                                                                <StyledTooltipBox>
                                                                    {plan.group.description && (
                                                                        <div>
                                                                            <Popover
                                                                                isModal={true}
                                                                                withArrow={true}
                                                                                trigger={<StyledInfoIcon />}
                                                                            >
                                                                                <StyledPopoverContent>
                                                                                    <Translate
                                                                                        isUseMainAppLanguage={true}
                                                                                        content={plan.group.description}
                                                                                    />
                                                                                </StyledPopoverContent>
                                                                            </Popover>
                                                                        </div>
                                                                    )}
                                                                </StyledTooltipBox>
                                                            </StyledInfoBox>
                                                        </StyledInfoContainer>

                                                        <StyledButton
                                                            // disabled={activeSubscription?.amount === plan.amount}
                                                            disabled={plan.is_locked}
                                                            onClick={() => {
                                                                if (!hasBillingData) {
                                                                    setShowBillingInfoModal(true)
                                                                } else {
                                                                    setSelectedMembership(plan)
                                                                    subscribeToHost({
                                                                        hostID: hostId,
                                                                        subscriptionId: +plan?.id,
                                                                        userDiscountId: plan?.discount?.id
                                                                    })
                                                                }
                                                            }}
                                                        >
                                                            <div>
                                                                {/* {!activeSubscription
                                                                    ? t('rookie_my_profile:membership:subscribe')
                                                                    : t('rookie_my_profile:membership:upgrade')} */}
                                                                <span>
                                                                    {t('rookie_my_profile:membership:subscribe')}
                                                                </span>
                                                                {hasCoupon && (
                                                                    <StyledDiscountText>
                                                                        {t(
                                                                            'rookie_my_profile:membership:for_percentage_off',
                                                                            {
                                                                                PERCENTAGE: plan.discount
                                                                                    ? +plan.discount
                                                                                          ?.discount_percentage * 100
                                                                                    : 0
                                                                            }
                                                                        )}
                                                                    </StyledDiscountText>
                                                                )}
                                                            </div>
                                                            <div>
                                                                <StyledButtonContent>
                                                                    <MicromorgiIcon />
                                                                    {(
                                                                        plan.amount *
                                                                        (hasCoupon
                                                                            ? plan.discount?.discount_percentage
                                                                                ? 1 -
                                                                                  +plan.discount?.discount_percentage
                                                                                : 0
                                                                            : 1)
                                                                    ).toFixed(0)}
                                                                    {`(${currencySymbol}${(
                                                                        plan.dollar_amount *
                                                                        (hasCoupon
                                                                            ? plan.discount?.discount_percentage
                                                                                ? 1 -
                                                                                  +plan.discount?.discount_percentage
                                                                                : 0
                                                                            : 1)
                                                                    ).toFixed(0)})`}
                                                                    {t('rookie_my_profile:membership:month')}
                                                                </StyledButtonContent>
                                                                {hasCoupon && (
                                                                    <StyledButtonContent discount={true}>
                                                                        <MicromorgiIcon />
                                                                        {plan.amount}
                                                                        {`(${currencySymbol}${plan.dollar_amount})`}
                                                                        {t('rookie_my_profile:membership:month')}
                                                                    </StyledButtonContent>
                                                                )}
                                                            </div>
                                                        </StyledButton>
                                                    </StyledContainer>
                                                </StyledContentBox>
                                            </StyledSplideSlide>
                                        )
                                    })}
                                </SplideTrack>
                            </StyledSplide>
                        </StyledContentContainer>
                    )}
                </div>
            )}
            {showSuccessModal && (
                <GiftModalSuccess
                    selectedMembership={selectedMembership as DonationType}
                    hostID={hostId}
                    handleCloseModal={() => handleToggleShowSuccessModal()}
                />
            )}
            {showBillingInfoModal && (
                <BillingInfoModal onClose={() => setShowBillingInfoModal(false)} onSendBillingInfoSuccess={onSuccess} />
            )}
        </>
    )
}
